import "@taxbit/react-sdk/style/inline.css";
import "./taxbit-theme.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// NOTE: this import only resolves to OUR React 17 because of patches/react+17.0.2.patch
// (adds an `exports` map so ESM deps can resolve `react/jsx-runtime` locally instead of
// walking up to the monorepo root's React 19). Removable once frontend is on React >= 18.
import { ClientTaxDocumentation, TaxbitQuestionnaire } from "@taxbit/react-sdk";
import { showNewMessage } from "@intercom/messenger-js-sdk";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import {
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import ContainedButton from "src/components/common/ContainedButton";
import {
  BORDER_LIGHT,
  Card,
  NAVY,
  SALMON,
  StatusPill,
  TEXT_SECONDARY,
  PillTone,
} from "./styles";
import { isSubjectToBackupWithholding } from "./backupWithholding";
import { buildTaxDocumentationPrefill } from "./taxDocumentationPrefill";
import { formatUtcDate } from "./utils";

type DocStatus = "NOT_STARTED" | "PENDING" | "VALID" | "ACTION_REQUIRED";

// "VALID" is the only state that counts as done; "PENDING" means submitted-but-validating.
const STATUS_META: Record<
  DocStatus,
  { label: string; tone: PillTone; blurb: string; rowValue: string; cta?: string }
> = {
  NOT_STARTED: {
    label: "Action required",
    tone: "warning",
    blurb:
      "Complete your W-9/W-8 tax form so we can report your crypto activity to the IRS accurately.",
    rowValue: "Not started",
    cta: "Start tax form",
  },
  PENDING: {
    label: "Validating",
    tone: "neutral",
    blurb:
      "Your tax form was submitted and is being validated with the IRS. This can take a little while — no action needed right now.",
    rowValue: "Validating",
  },
  VALID: {
    label: "Form valid",
    tone: "success",
    blurb: "Your US taxpayer certification is on file. Update it if your circumstances change.",
    rowValue: "On file",
    cta: "View / update form",
  },
  ACTION_REQUIRED: {
    label: "Action required",
    tone: "error",
    blurb:
      "There was a problem with your submitted tax info (e.g. a TIN mismatch). Please review and re-submit.",
    rowValue: "Needs attention",
    cta: "Fix tax form",
  },
};

const BACKUP_WITHHOLDING_SUPPORT_MESSAGE =
  "Hi, the IRS has told me I'm subject to backup withholding and I need help with my W-9.";

const UNAVAILABLE_META: (typeof STATUS_META)[DocStatus] = {
  label: "Unavailable",
  tone: "neutral",
  blurb: "We couldn't load your tax documentation status right now. Please try again shortly.",
  rowValue: "—",
  cta: "Try again",
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: NAVY }}>{value}</Typography>
  </Stack>
);

const TaxDocumentationWidget = () => {
  const dispatch = useAppDispatch();
  const statusQuery = api.useGetTaxDocumentationStatusQuery();
  const [mintToken, tokenState] = api.useLazyGetTaxDocumentationTokenQuery();
  const { details, address } = useUserSlice();
  const prefill = useMemo(
    () => buildTaxDocumentationPrefill(details, address),
    [details, address],
  );

  const [open, setOpen] = useState(false);
  const [bearerToken, setBearerToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [sdkAuthFailed, setSdkAuthFailed] = useState(false);
  const [backupWithholdingBlocked, setBackupWithholdingBlocked] = useState(false);
  // A genuine token expiry needs exactly one re-mint; more means TaxBit is rejecting fresh
  // tokens, and retrying further would loop forever.
  const remintAttempts = useRef(0);

  const status: DocStatus = statusQuery.data?.status ?? "NOT_STARTED";
  // Without data we don't know the status, so don't claim the form is outstanding.
  const statusUnavailable = statusQuery.isError && !statusQuery.data;
  const meta = statusUnavailable ? UNAVAILABLE_META : STATUS_META[status];
  const validatedOn = statusQuery.data?.tinValidationDate;
  // ACTION_REQUIRED → re-open the form pre-filled but editable so the user can correct it.
  // Otherwise lock what's already valid: KYC details (address, name) change through KYC, not here.
  const adaptiveMode = status === "ACTION_REQUIRED" ? "full" : "skipLock";

  const fetchToken = useCallback(async () => {
    const result = await mintToken().unwrap();
    setBearerToken(result.access_token);
  }, [mintToken]);

  const handleOpen = async () => {
    setSubmitted(false);
    setSdkAuthFailed(false);
    remintAttempts.current = 0;
    setOpen(true);
    try {
      await fetchToken();
    } catch {
      // tokenState.isError surfaces the failure in the dialog body
    }
  };

  const handleClose = () => {
    setOpen(false);
    setBearerToken(null);
    // Pull the latest status — it may now be PENDING/VALID after a submission.
    void statusQuery.refetch();
    // Opening the form provisions the TaxBit account, so a cached 404 on it is now stale.
    dispatch(api.util.invalidateTags(["TaxAccount"]));
  };

  // Re-mint once on a 401. If the fresh token is also rejected it's configuration, not expiry.
  const handleSdkError = useCallback(
    async (error: Error) => {
      if (/401|403|unauthor|forbidden/i.test(error?.message ?? "")) {
        if (remintAttempts.current >= 1) {
          setBearerToken(null);
          setSdkAuthFailed(true);
          return;
        }
        remintAttempts.current += 1;
        setBearerToken(null);
        await fetchToken().catch(() => undefined);
      }
    },
    [fetchToken],
  );

  // Throwing stops the SDK posting it; patches/@taxbit+react-sdk+3.6.1.patch makes that a clean cancel.
  const handleSubmit = (data: ClientTaxDocumentation) => {
    if (isSubjectToBackupWithholding(data)) {
      setBackupWithholdingBlocked(true);
      throw new Error("Submission blocked: subject to backup withholding");
    }
  };

  const handleBackToForm = () => setBackupWithholdingBlocked(false);

  // Leave the form open underneath: Intercom may not have booted, and their answers shouldn't be lost.
  const handleBackupWithholdingSupport = () => {
    setBackupWithholdingBlocked(false);
    showNewMessage(BACKUP_WITHHOLDING_SUPPORT_MESSAGE);
  };

  const handleSuccess = useCallback(() => {
    // onSuccess only means TaxBit received it, not that the TIN matched — the webhook and a
    // refetch resolve the true state.
    setSubmitted(true);
    void statusQuery.refetch();
    dispatch(api.util.invalidateTags(["TaxAccount"]));
  }, [statusQuery, dispatch]);

  useEffect(() => {
    if (!open) return;
    return () => setSubmitted(false);
  }, [open]);

  return (
    <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.75, height: "100%" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
        <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
          Tax documentation
        </Typography>
        {statusQuery.isLoading ? (
          <Skeleton variant="rounded" width={88} height={24} />
        ) : (
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        )}
      </Stack>

      {statusQuery.isLoading ? (
        <Skeleton variant="rounded" height={96} />
      ) : (
        <>
          <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
            {meta.blurb}
          </Typography>
          <Stack spacing={1.25} sx={{ mt: "auto", pt: 1.75, borderTop: `1px solid ${BORDER_LIGHT}` }}>
            <Row label="Form W-9 / W-8" value={meta.rowValue} />
            {validatedOn && <Row label="Validated" value={formatUtcDate(validatedOn)} />}
            {meta.cta &&
              (status === "VALID" ? (
                <Stack direction="row">
                  <ButtonBase
                    onClick={() => void handleOpen()}
                    sx={{
                      fontFamily: "Roobert, sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      color: SALMON,
                      "&:hover": { color: NAVY },
                    }}>
                    {meta.cta}
                  </ButtonBase>
                </Stack>
              ) : (
                <Stack direction="row" sx={{ pt: 0.75 }}>
                  <ContainedButton
                    color="salmon"
                    width="160px"
                    height="38px"
                    onClick={() => void (statusUnavailable ? statusQuery.refetch() : handleOpen())}>
                    {meta.cta}
                  </ContainedButton>
                </Stack>
              ))}
          </Stack>
        </>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Tax documentation</DialogTitle>
        <DialogContent dividers>
          {submitted ? (
            <Stack spacing={1.5} alignItems="center" py={4}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Submission received
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Thanks — your form is being validated with the IRS. We'll update your status here
                once it completes.
              </Typography>
              <Button variant="contained" onClick={handleClose}>
                Done
              </Button>
            </Stack>
          ) : sdkAuthFailed ? (
            <Stack spacing={1.5} py={4} alignItems="center">
              <Typography variant="body2" color="error.main">
                The tax form service rejected our credentials. Please try again later or contact
                support if this keeps happening.
              </Typography>
              <Button variant="contained" onClick={handleClose}>
                Close
              </Button>
            </Stack>
          ) : tokenState.isLoading || (!bearerToken && !tokenState.isError) ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
            </Stack>
          ) : tokenState.isError ? (
            <Stack spacing={1.5} py={4} alignItems="center">
              <Typography variant="body2" color="error.main">
                We couldn't start the tax form right now. Please try again.
              </Typography>
              <Button variant="contained" onClick={() => void fetchToken()}>
                Retry
              </Button>
            </Stack>
          ) : (
            bearerToken && (
              <TaxbitQuestionnaire
                // Key on the token so a re-mint forces a full remount; prop swaps retain
                // stale state (cached 401s).
                key={bearerToken}
                questionnaire="W-FORM"
                bearerToken={bearerToken}
                data={prefill}
                adaptiveMode={adaptiveMode}
                realTimeTinValidation
                onSubmit={handleSubmit}
                onSuccess={() => void handleSuccess()}
                onError={(error) => void handleSdkError(error)}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={backupWithholdingBlocked} onClose={handleBackToForm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Are you subject to backup withholding?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Only untick this box if you have been notified by the IRS. If you have, we&rsquo;ll be
            required to withhold 24% of the proceeds from your sales and send it to the IRS, so
            please contact support. Otherwise, go back and tick the box to continue.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleBackupWithholdingSupport}>Contact support</Button>
          <Button variant="contained" onClick={handleBackToForm}>
            Back to form
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TaxDocumentationWidget;
